import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

interface RedirectTest {
  path: string;
  result?: {
    found: boolean;
    target_path?: string;
    status_code?: number;
    error?: string;
  };
  loading?: boolean;
}

const TestRedirects: React.FC = () => {
  const [testPath, setTestPath] = useState('/article/holiday-elf-working-parents-time-saving-tips');
  const [tests, setTests] = useState<RedirectTest[]>([]);

  const testRedirect = async (path: string) => {
    const testId = Date.now();
    const newTest: RedirectTest = { path, loading: true };
    
    setTests(prev => [newTest, ...prev]);

    try {
      // Test the Edge Function directly
      const response = await fetch(
        `/api/handle-redirect`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ path })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      setTests(prev => prev.map(test => 
        test === newTest 
          ? { 
              ...test, 
              loading: false,
              result: {
                found: !!data.target_path,
                target_path: data.target_path,
                status_code: data.status_code,
              }
            }
          : test
      ));
    } catch (error) {
      setTests(prev => prev.map(test => 
        test === newTest 
          ? { 
              ...test, 
              loading: false,
              result: {
                found: false,
                error: error instanceof Error ? error.message : 'Unknown error'
              }
            }
          : test
      ));
    }
  };

  const commonTestPaths = [
    '/article/holiday-elf-working-parents-time-saving-tips',
    '/article/elf-warning-letters-guide-templates-examples',
    '/article/holiday-elf-name-generator',
    '/article/creative-holiday-elf-arrival-ideas-2024',
    '/article/printable-holiday-elf-letterhead-templates-guide',
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-4">Redirect System Test</h1>
        <p className="text-gray-600">
          Test the URL redirect system to ensure old /article/ URLs properly redirect to /blog/ URLs.
        </p>
      </div>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Test a Redirect</CardTitle>
          <CardDescription>
            Enter a path to test if it has a redirect configured
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Input
              value={testPath}
              onChange={(e) => setTestPath(e.target.value)}
              placeholder="/article/some-article-slug"
              className="flex-1"
            />
            <Button 
              onClick={() => testRedirect(testPath)}
              disabled={!testPath.trim()}
            >
              Test Redirect
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Quick Tests</CardTitle>
          <CardDescription>
            Test common article URLs that should be redirected
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {commonTestPaths.map((path) => (
              <Button
                key={path}
                variant="outline"
                size="sm"
                onClick={() => testRedirect(path)}
                className="text-left justify-start"
              >
                {path}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Test Results</CardTitle>
          <CardDescription>
            Results of redirect tests (most recent first)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {tests.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              No tests run yet. Try testing a redirect above.
            </p>
          ) : (
            <div className="space-y-4">
              {tests.map((test, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm flex-1">
                      {test.path}
                    </code>
                    {test.loading ? (
                      <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                    ) : test.result?.found ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                  </div>
                  
                  {test.loading ? (
                    <p className="text-sm text-gray-500">Testing...</p>
                  ) : test.result?.error ? (
                    <Alert variant="destructive">
                      <AlertDescription>
                        Error: {test.result.error}
                      </AlertDescription>
                    </Alert>
                  ) : test.result?.found ? (
                    <div className="text-sm">
                      <p className="text-green-600 font-medium">✅ Redirect found!</p>
                      <p><strong>Target:</strong> {test.result.target_path}</p>
                      <p><strong>Status Code:</strong> {test.result.status_code}</p>
                    </div>
                  ) : (
                    <p className="text-sm text-red-600">❌ No redirect configured for this path</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TestRedirects; 